import AppConstants from "../../constants";
import networkHandler from "../../network/networkHandler";
import networkHandlerReporting from "../../network/networkHandlerReporting";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { EApiMiddlewareMethods } from "../../network/networkHandler.types";
import { UserLoginState } from "./loginSlice.types";
import { matchAllElements, getJWTToken } from "../../utils/helpers.utils";
import { emptySessionStorage, setSessionStorage } from "../LoginUtils";
import { UserRoleConstants } from "constants/UserRolesConstants";
import { storeCountryUrlsInSession } from "utils/urlUtils";
import { COUNTRY_URL } from "constants/CommonConstants";
import { clearAllHeaders, storeHeadersFromApi } from "utils/storageUtils";


const fetchAccessControl = async (authHeader: string, countryCode: string) => {
  const request = {
    method: EApiMiddlewareMethods.GET,
    url: `/access-control`,
    headers: {
      Authorization: authHeader,
      countryISOCode: countryCode,
    },
  } as any;
  const { data } = await networkHandler(request, { isUser: true });
  return data;
};

export const fetchUserAccessControl = createAsyncThunk(
  "UserLogin/fetchUserAccessControl",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchAccessControl(
        sessionStorage.getItem("token") as string,
        sessionStorage.getItem(AppConstants.COUNTRY_CODE) as string
      );
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const userLogin = createAsyncThunk("UserLogin/Login", async ({ userdata }: { userdata: any }, { rejectWithValue }) => {
  try {
    const request = {
      url: "v3/authenticate/verify-otp",
      method: EApiMiddlewareMethods.POST,
      data: userdata,
    } as any;
    const { data } = await networkHandler(request, { isUser: true, isLogin: true });
    let userResponse = data;
    try {
      const countryRequest = {
        url: COUNTRY_URL,
        method: EApiMiddlewareMethods.GET,
      } as any;
      countryRequest.headers = {
        countryISOCode: userResponse.countryCode,
        Authorization: `Bearer ${getJWTToken(userResponse)}`,
      };
      const { data: countryData } = await networkHandlerReporting(countryRequest);
      let countryUrl = countryData?.url?.wings ? countryData.url.wings : countryData?.countryUrl ? countryData.countryUrl : "";
      storeHeadersFromApi(countryData?.headers);
      sessionStorage.setItem(AppConstants.COUNTRY_URL, countryUrl);
      // Store all URLs from countryData.url in sessionStorage
      if (countryData?.url && typeof countryData.url === "object") {
        storeCountryUrlsInSession(countryData.url);
      }
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
    if (data && (data.jwt || data.jwtToken))  {
      try {
        const jwt = getJWTToken(data);
        data.uacData = await fetchAccessControl(`Bearer ${jwt}`, userResponse.countryCode);
      } catch (error: any) {
        console.error("Failed to fetch access control:", error);
      }
    }
    return data;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data);
  }
});

export const newUserLogin = createAsyncThunk("UserLogin/newUserLogin", async ({ userdata }: { userdata: any }, { rejectWithValue }) => {
  try {
    const request = {
      url: "v3/authenticate",
      method: EApiMiddlewareMethods.POST,
      data: userdata,
    } as any;
    const { data } = await networkHandler(request, { isUser: true, isLogin: true });
    const jwt = getJWTToken(data)
    if (jwt) {
      let userResponse = data;
      try {
        const countryRequest = { url: COUNTRY_URL, method: EApiMiddlewareMethods.GET } as any;
        /**
         * Entry point of login, hence using JWT token directly from the response instead of session.
        */
        countryRequest.headers = { countryISOCode: userResponse.countryCode, Authorization: `Bearer ${jwt}` };
        const { data: countryData } = await networkHandlerReporting(countryRequest);
        let countryUrl = countryData?.url?.wings ? countryData.url.wings : countryData?.countryUrl ? countryData.countryUrl : "";
        storeHeadersFromApi(countryData?.headers);
        sessionStorage.setItem(AppConstants.COUNTRY_URL, countryUrl);
        // Store all URLs from countryData.url in sessionStorage
      if (countryData?.url && typeof countryData.url === "object") {
        storeCountryUrlsInSession(countryData.url);
      }
      } catch (error: any) {
        return rejectWithValue(error?.response?.data);
      }
      // Fetch access control data
      try {
        data.uacData = await fetchAccessControl(`Bearer ${jwt}`, userResponse.countryCode);
      } catch (error: any) {
        console.error("Failed to fetch access control:", error);
      }
    }
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

export const userLogout = createAsyncThunk("UserLogin/Logout", () => {
  return false;
});

export const resetUserResetPwd = createAsyncThunk("UserLogin/ResetUserResetPassword", () => {
  return false;
});

export const resetUserError = createAsyncThunk("UserLogin/ResetUserError", () => {
  return "";
});

export const userResetPwd = createAsyncThunk("UserLogin/ResetPassword", async ({ userdata }: { userdata: any }, { rejectWithValue }) => {
  try {
    const request = {
      url: "user/expiry/resetPassword",
      method: EApiMiddlewareMethods.POST,
      data: userdata,
    } as any;
    const { data } = await networkHandler(request,  { isUser: true });
    return data;
  } catch (error: any) {
    return rejectWithValue(error.response.data);
  }
});

const initialState: UserLoginState = {
  isLoggedIn: sessionStorage.getItem("token") ? true : false,
  loading: false,
  errorCode: "",
  countryCode: "",
  error: "",
  username: "",
  resetPwdSuccess: false,
  resetPwdMsg: "",
  isAdmin: false,
  isCollectionPointManager: false,
  isCHDManager: false,
  isVTManager: false,
  isQCManager: false,
  isDriver: false,
  isLastMileViewer: false,
  isDispatcher: false,
  isOnlyExpressDispatcher: false,
  isOnlyStandardDispatcher: false,
  isOnlyStdLastMileViewer: false,
  isOnlyExpLastMileViewer: false,
  role: "",
  showOtp: false,
  uacData: null,
  uacLoading: false,
  uacError: null,
};

const userLoginSlice = createSlice({
  name: "UserLogin",
  initialState,
  reducers: {},
  extraReducers: (builders) => {
    builders
      .addCase(newUserLogin.pending, (state) => {
        state.showOtp = false;
        state.errorCode = "";
        state.error = "";
        emptySessionStorage();
        clearAllHeaders();
        state.countryCode = "";
        state.token = "";
        state.isLoggedIn = false;
        state.loading = true;
      })
      .addCase(newUserLogin.fulfilled, (state, action) => {
        const { payload, meta: { arg: { userdata }}} = action;
        const jwt = getJWTToken(payload);
        if (jwt && payload?.countryCode) {
          // Skip OTP
          setSessionStorage(payload);
          const token = `Bearer ${jwt}`;
          state.countryCode = payload.countryCode;
          state.token = token;
          state.username = userdata.username;
          state.isLoggedIn = true;
          state.role = payload?.roles?.length > 0 && payload.roles[0].code;
          const serviceType = payload.serviceTypes;

          if (payload?.roles?.length > 0 && payload.roles[0].code === "ROLE_LAST_MILE_DISPATCHER") {
            if (matchAllElements(["STANDARD", "EXPRESS"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_DISPATCHER");
            }
            if (!matchAllElements(["STANDARD"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_DISPATCHER_EXPRESS");
              state.role = AppConstants.EXPRESS_DISPATCHER_ROLE;
            }
            if (!matchAllElements(["EXPRESS"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_DISPATCHER_STANDARD");
            }
          }

          if (payload?.roles?.length > 0 && payload.roles[0].code === "ROLE_LAST_MILE_VIEWER") {
            if (matchAllElements(["STANDARD", "EXPRESS"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_VIEWER");
            }
            if (!matchAllElements(["STANDARD"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_VIEWER_EXPRESS");
              state.role = AppConstants.EXPRESS_VIEWER_ROLE;
            }
            if (!matchAllElements(["EXPRESS"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_VIEWER_STANDARD");
            }
          }
          if (payload.uacData) {
            state.uacData = payload.uacData;
          }
          state.loading = false;
        } else if (payload?.error) {
          state.errorCode = payload.error || AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
          if (state.errorCode === AppConstants.LOGIN_CONSTANTS.RESPONSE_CONSTANTS.ERROR_CODES.PWD_EXP) {
            sessionStorage.setItem("username", userdata.username);
          }
          state.error = payload ? payload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
          state.isLoggedIn = false;
          state.loading = false;
        } else {
          state.showOtp = true;
          sessionStorage.setItem("username", userdata?.username);
          state.loading = false;
        }
      })
      .addCase(newUserLogin.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
        state.error = errorPayload ? errorPayload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
        state.loading = false;
      })
      /** User Login */
      /** */
      .addCase(userLogin.pending, (state) => {
        emptySessionStorage();
        clearAllHeaders();
        state.errorCode = "";
        state.error = "";
        state.countryCode = "";
        state.token = "";
        state.isLoggedIn = false;
        state.loading = true;
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        const {
          payload,
          meta: {
            arg: { userdata },
          },
        } = action;
        state.showOtp = false;
        if (payload.error) {
          sessionStorage.setItem("token", "");
          sessionStorage.setItem("name", "");
          sessionStorage.setItem("role", "[]");
          sessionStorage.setItem("images", "");
          sessionStorage.setItem(AppConstants.COUNTRY_CODE, "");
          state.countryCode = "";
          state.token = "";
          state.errorCode = payload.error || AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
          if (state.errorCode === AppConstants.LOGIN_CONSTANTS.RESPONSE_CONSTANTS.ERROR_CODES.PWD_EXP) {
            sessionStorage.setItem("username", userdata.username);
          }
          state.error = payload ? payload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
          state.isLoggedIn = false;
        } else {
          const token = `Bearer ${getJWTToken(payload)}`;
          sessionStorage.setItem("token", token);
          sessionStorage.setItem("name", payload.name);
          sessionStorage.setItem("role", JSON.stringify(payload.roles));
          sessionStorage.setItem("images", JSON.stringify(payload.images || ""));
          sessionStorage.setItem(AppConstants.COUNTRY_CODE, payload.countryCode);
          state.countryCode = payload.countryCode;
          state.errorCode = "";
          state.error = "";
          state.token = token;
          state.username = userdata.username;
          state.isLoggedIn = true;
          state.role = payload.roles[0].code;
          const serviceType = payload.serviceTypes;
          /* if (payload.roles[0].code === "ROLE_LAST_MILE_DISPATCHER") {
            state.isDispatcher = matchAllElements(["STANDARD", "EXPRESS"], serviceType);
            state.isOnlyExpressDispatcher = !matchAllElements(["STANDARD"], serviceType);
            state.isOnlyStandardDispatcher = !matchAllElements(["EXPRESS"], serviceType);
            state.role = !matchAllElements(["STANDARD"], serviceType)
              ? "ROLE_LAST_MILE_DISPATCHER_EXPRESS"
              : "ROLE_LAST_MILE_DISPATCHER";
          } */
          if (payload.roles[0].code === "ROLE_LAST_MILE_DISPATCHER") {
            if (matchAllElements(["STANDARD", "EXPRESS"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_DISPATCHER");
            }
            if (!matchAllElements(["STANDARD"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_DISPATCHER_EXPRESS");
              state.role = AppConstants.EXPRESS_DISPATCHER_ROLE;
            }
            if (!matchAllElements(["EXPRESS"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_DISPATCHER_STANDARD");
            }
          }
          if (payload.roles[0].code === "ROLE_LAST_MILE_VIEWER") {
            if (matchAllElements(["STANDARD", "EXPRESS"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_VIEWER");
            }
            if (!matchAllElements(["STANDARD"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_VIEWER_EXPRESS");
              state.role = AppConstants.EXPRESS_VIEWER_ROLE;
            }
            if (!matchAllElements(["EXPRESS"], serviceType)) {
              sessionStorage.setItem("currentRole", "LAST_MILE_VIEWER_STANDARD");
            }
          }
          if (payload.uacData) {
            state.uacData = payload.uacData;
          }
          state.loading = false;
        }
      })
      .addCase(userLogin.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
        state.error = errorPayload ? errorPayload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
        state.loading = false;
      })
      .addCase(fetchUserAccessControl.pending, (state) => {
        state.uacLoading = true;
        state.uacError = null;
      })
      .addCase(fetchUserAccessControl.fulfilled, (state, action) => {
        state.uacLoading = false;
        state.uacData = action.payload;
        state.uacError = null;
      })
      .addCase(fetchUserAccessControl.rejected, (state, action) => {
        state.uacLoading = false;
        state.uacError = action.payload as string;
      })
      .addCase(userResetPwd.pending, (state) => {
        state.loading = true;
        state.errorCode = "";
        state.error = "";
      })
      .addCase(userResetPwd.fulfilled, (state, action) => {
        const payload: any = action.payload;
        state.loading = false;
        if (payload.error) {
          state.resetPwdSuccess = false;
          state.errorCode = payload.error || AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
          state.error = payload ? payload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
        } else {
          sessionStorage.setItem("token", "");
          sessionStorage.setItem("username", "");
          sessionStorage.setItem("name", "");
          sessionStorage.setItem("role", "[]");
          sessionStorage.setItem("images", "");
          sessionStorage.setItem(AppConstants.COUNTRY_CODE, "");
          state.countryCode = "";
          state.errorCode = "";
          state.error = "";
          state.resetPwdSuccess = true;
          state.resetPwdMsg = payload;
        }
      })
      .addCase(userResetPwd.rejected, (state, action) => {
        const errorPayload: any = action.payload;
        state.loading = false;
        state.resetPwdSuccess = false;
        state.errorCode = errorPayload ? errorPayload.error : AppConstants.RESPONSE_CONSTANTS.ERROR_CODES.DEFAULT_API_FAILED;
        state.error = errorPayload ? errorPayload.message : AppConstants.RESPONSE_CONSTANTS.DEFAULT_API_FAILED_ERROR_MSG;
      })
      .addCase(resetUserResetPwd.fulfilled, (state, action) => {
        const { payload } = action;
        state.resetPwdSuccess = payload;
      })
      .addCase(resetUserError.fulfilled, (state, action) => {
        const { payload } = action;
        state.errorCode = payload;
        state.error = payload;
      })
      .addCase(userLogout.fulfilled, (state, action) => {
        const { payload } = action;
        sessionStorage.setItem("token", "");
        sessionStorage.setItem("username", "");
        sessionStorage.setItem("name", "");
        sessionStorage.setItem("role", "[]");
        sessionStorage.setItem("images", "");
        sessionStorage.setItem(AppConstants.HUB_CODE, "[]");
        sessionStorage.setItem(AppConstants.COUNTRY_CODE, "");
        sessionStorage.setItem("currentRole", "");
        clearAllHeaders();
        state.countryCode = "";
        state.loading = false;
        state.errorCode = "";
        state.error = "";
        state.token = "";
        state.username = "";
        state.isAdmin = false;
        state.isCHDManager = false;
        state.isVTManager = false;
        state.isCollectionPointManager = false;
        state.isDriver = false;
        state.isLoggedIn = payload;
        state.isOnlyExpressDispatcher = false;
        state.isOnlyStandardDispatcher = false;
        state.isDispatcher = false;
        state.isLastMileViewer = false;
        state.isOnlyStdLastMileViewer = false;
        state.isOnlyExpLastMileViewer = false;
        state.uacData = null;
      });
      
  },
});

export default userLoginSlice.reducer;

